/**
 * useWebSocketEventLogger - Hook for capturing and logging WebSocket events
 *
 * This hook provides functionality to capture WebSocket messages (sent and received)
 * and maintain a log of events for debugging purposes.
 */

import { useCallback, useRef, useState } from "react";
import type { WebSocketEvent } from "../components/Debug/WebSocketEventLogger";

export function useWebSocketEventLogger() {
  const [events, setEvents] = useState<WebSocketEvent[]>([]);
  const eventIdCounter = useRef(0);

  const logEvent = useCallback(
    (
      direction: "sent" | "received",
      type: string,
      data: unknown,
      error?: string,
    ) => {
      const event: WebSocketEvent = {
        id: `ws-event-${++eventIdCounter.current}`,
        timestamp: Date.now(),
        direction,
        type,
        data,
        error,
      };

      setEvents((prev) => [...prev, event]);
    },
    [],
  );

  const logSentMessage = useCallback(
    (type: string, data: unknown) => {
      logEvent("sent", type, data);
    },
    [logEvent],
  );

  const logReceivedMessage = useCallback(
    (type: string, data: unknown) => {
      logEvent("received", type, data);
    },
    [logEvent],
  );

  const logError = useCallback(
    (
      direction: "sent" | "received",
      type: string,
      data: unknown,
      error: string,
    ) => {
      logEvent(direction, type, data, error);
    },
    [logEvent],
  );

  const clearEvents = useCallback(() => {
    setEvents([]);
    eventIdCounter.current = 0;
  }, []);

  // Create a wrapper function that can be used to intercept WebSocket send calls
  const createSendWrapper = useCallback(
    (originalSend: (data: string) => void) => {
      return (data: string) => {
        try {
          const parsedData = JSON.parse(data);
          logSentMessage(parsedData.type || "UNKNOWN", parsedData);
        } catch (error) {
          logError(
            "sent",
            "PARSE_ERROR",
            data,
            `Failed to parse sent message: ${error}`,
          );
        }
        return originalSend(data);
      };
    },
    [logSentMessage, logError],
  );

  // Create a wrapper function that can be used to intercept WebSocket message events
  const createMessageWrapper = useCallback(
    (originalOnMessage: ((event: MessageEvent) => void) | null) => {
      return (event: MessageEvent) => {
        try {
          const parsedData = JSON.parse(event.data);
          logReceivedMessage(parsedData.type || "UNKNOWN", parsedData);
        } catch (error) {
          logError(
            "received",
            "PARSE_ERROR",
            event.data,
            `Failed to parse received message: ${error}`,
          );
        }

        // Call the original handler if it exists
        if (originalOnMessage) {
          originalOnMessage(event);
        }
      };
    },
    [logReceivedMessage, logError],
  );

  return {
    events,
    clearEvents,
    logSentMessage,
    logReceivedMessage,
    logError,
    createSendWrapper,
    createMessageWrapper,
  };
}
